/**
 * Supabase Insights Repository
 * Handles CRUD operations for insights via Supabase
 */

import type {
  IInsightsRepository,
  Insight,
  CreateInsightData,
  UpdateInsightData,
} from '../interfaces/IInsightsRepository'
import type { CursorParams, PaginatedResult } from '@/lib/types/pagination'
import { requireSupabaseClient, type InsightWithProfile } from '@/lib/supabase'

export class SupabaseInsightsRepository implements IInsightsRepository {
  /**
   * Get all insights, sorted by most recent
   */
  async getInsights(): Promise<Insight[]> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('insights')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          is_admin
        )
      `
      )
      .order('created_at', { ascending: false })
      .returns<InsightWithProfile[]>()

    if (error) {
      throw new Error(`Failed to fetch insights: ${error.message}`)
    }

    return (data || []).map((row) => this.mapToInsight(row))
  }

  /**
   * Get paginated insights with cursor-based pagination and server-side filters
   */
  async getInsightsPaginated(
    params: CursorParams
  ): Promise<PaginatedResult<Insight>> {
    const supabase = requireSupabaseClient()
    const { cursor, cursorId, limit, search, sentiment } = params

    let query = supabase
      .from('insights')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          is_admin
        )
      `
      )
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(limit + 1)

    // Cursor filter for pagination
    if (cursor && cursorId) {
      query = query.or(
        `created_at.lt.${cursor},and(created_at.eq.${cursor},id.lt.${cursorId})`
      )
    }

    // Server-side search filter (content + ticker)
    if (search) {
      const escaped = search.replace(/%/g, '\\%').replace(/_/g, '\\_')
      query = query.or(
        `content.ilike.%${escaped}%,ticker.ilike.%${escaped}%`
      )
    }

    // Server-side sentiment filter
    if (sentiment) {
      query = query.eq('sentiment', sentiment)
    }

    const { data, error } = await query.returns<InsightWithProfile[]>()

    if (error) {
      throw new Error(`Failed to fetch insights: ${error.message}`)
    }

    const rows = data || []
    const hasMore = rows.length > limit
    const pageRows = hasMore ? rows.slice(0, limit) : rows
    const lastRow = pageRows[pageRows.length - 1]

    return {
      data: pageRows.map((row) => this.mapToInsight(row)),
      nextCursor: lastRow ? lastRow.created_at : null,
      nextCursorId: lastRow ? lastRow.id : null,
      hasMore,
    }
  }

  /**
   * Get single insight by ID
   */
  async getInsightById(id: string): Promise<Insight | null> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('insights')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          is_admin
        )
      `
      )
      .eq('id', id)
      .single<InsightWithProfile>()

    if (error) {
      if (error.code === 'PGRST116') return null // Not found
      throw new Error(`Failed to fetch insight: ${error.message}`)
    }

    return this.mapToInsight(data)
  }

  /**
   * Get insights for specific user
   */
  async getUserInsights(userId: string): Promise<Insight[]> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('insights')
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          is_admin
        )
      `
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .returns<InsightWithProfile[]>()

    if (error) {
      throw new Error(`Failed to fetch user insights: ${error.message}`)
    }

    return (data || []).map((row) => this.mapToInsight(row))
  }

  /**
   * Create new insight
   */
  async createInsight(input: CreateInsightData): Promise<Insight> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase
      .from('insights')
      .insert({
        user_id: input.userId,
        content: input.content,
        sentiment: input.sentiment,
        ticker: input.ticker || null,
        tags: input.tags || [],
        image_url: input.imageUrl || null,
      } as Record<string, unknown>)
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          is_admin
        )
      `
      )
      .single<InsightWithProfile>()

    if (error) {
      throw new Error(`Failed to create insight: ${error.message}`)
    }

    return this.mapToInsight(data)
  }

  /**
   * Update existing insight
   */
  async updateInsight(id: string, input: UpdateInsightData): Promise<Insight> {
    const supabase = requireSupabaseClient()

    const updateData: Record<string, unknown> = {}
    if (input.content !== undefined) updateData.content = input.content
    if (input.sentiment !== undefined) updateData.sentiment = input.sentiment
    if (input.ticker !== undefined) updateData.ticker = input.ticker
    if (input.tags !== undefined) updateData.tags = input.tags
    if (input.imageUrl !== undefined) updateData.image_url = input.imageUrl

    const { data, error } = await supabase
      .from('insights')
      .update(updateData as Record<string, unknown>)
      .eq('id', id)
      .select(
        `
        *,
        profiles:user_id (
          username,
          display_name,
          is_admin
        )
      `
      )
      .single<InsightWithProfile>()

    if (error) {
      throw new Error(`Failed to update insight: ${error.message}`)
    }

    return this.mapToInsight(data)
  }

  /**
   * Delete insight
   */
  async deleteInsight(id: string): Promise<void> {
    const supabase = requireSupabaseClient()

    const { error } = await supabase.from('insights').delete().eq('id', id)

    if (error) {
      throw new Error(`Failed to delete insight: ${error.message}`)
    }
  }

  /**
   * Map database row to Insight type
   */
  private mapToInsight(row: InsightWithProfile): Insight {
    return {
      id: row.id,
      userId: row.user_id,
      username: row.profiles?.username || 'unknown',
      displayName: row.profiles?.display_name || undefined,
      content: row.content,
      sentiment: row.sentiment,
      ticker: row.ticker || undefined,
      tags: row.tags || [],
      imageUrl: row.image_url || undefined,
      createdAt: new Date(row.created_at),
      isAdmin: row.profiles?.is_admin || false,
      trackCount: row.track_count || 0,
    }
  }
}
